import React from 'react';
import styled from 'styled-components';
import { useAccount, useChainId } from 'wagmi';
import WalletConnect from '../WalletConnect';
import { useDisconnect } from 'wagmi';
import { SUPPORTED_CHAINS } from '../../utils/relayProtocol';

// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';
const ETH_DARK = '#333333'; // Dark gray for text
const ETH_LIGHT = '#FFFFFF'; // White for background
const ETH_BLACK = '#000000';

const NavigationBar = styled.div`
  width: 100%;
  background: ${ETH_LIGHT};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const NavigationContainer = styled.div`
  width: 100%;
  padding: 16px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    gap: 12px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  @media (max-width: 700px) {
    flex: 1;
  }
`;

const EthLogo = styled.img`
  height: 40px;
  width: auto;
  object-fit: contain;
  
  @media (max-width: 700px) {
    height: 32px;
  }
`;

const LogoText = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: ${ETH_DARK};
  letter-spacing: -0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 700px) {
    font-size: 14px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const WalletSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WalletBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(98, 126, 234, 0.1);
  border: 1px solid rgba(98, 126, 234, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(98, 126, 234, 0.2);
    border-color: rgba(98, 126, 234, 0.5);
  }
`;

const ChainLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  object-fit: cover;
`;

const AddressText = styled.span`
  color: ${ETH_DARK};
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', monospace;
  
  @media (max-width: 700px) {
    font-size: 12px;
  }
`;


const EthNavBar: React.FC = () => {
  const { isConnected, address } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();

  // Function to get current chain logo - always show ETH logo on ETH pages
  const getCurrentChainLogo = () => {
    // Always show ETH logo on ETH pages, or if connected to ETH chain
    if (chainId === 1) {
      return 'https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg';
    }
    // If on different chain but on ETH page, still show ETH logo
    const chain = Object.values(SUPPORTED_CHAINS).find(chain => chain.chainId === chainId);
    // Default to ETH logo since we're on ETH pages
    return chain?.logo || 'https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg';
  };

  return (
    <NavigationBar>
      <NavigationContainer>
        <LogoSection onClick={() => window.location.href = '/eth'}>
          <EthLogo src="/eth-world-computer.png" alt="Ethereum World Computer" />
          <LogoText>ethereum: the world computer</LogoText>
        </LogoSection>

        <WalletSection>
          {isConnected && address && (
            <>
              <WalletBadge onClick={() => disconnect()} title="Click to disconnect wallet">
                <AddressText>
                  {address.slice(0, 6)}...{address.slice(-4)}
                </AddressText>
              </WalletBadge>
              <ChainLogo src={getCurrentChainLogo()} alt="Current Chain" />
            </>
          )}
          {!isConnected && <WalletConnect />}
        </WalletSection>
      </NavigationContainer>
    </NavigationBar>
  );
};

export default EthNavBar;
